/**
 * Future Closure Live Preview Component
 * P2: Shows real-time estimate as user adjusts parameters
 */

import React, { useState, useEffect, useCallback } from 'react';

interface LivePreviewProps {
  aggression: number;
  baseMonths: number;
  useBestBase: boolean;
  closureDuration: number;
  customContinuations?: {
    officer_salary: number;
    employee_wages: number;
    rent: number;
    utilities: number;
    insurance: number;
    other_expenses: number;
  };
  reportData?: any;
}

export default function FutureClosureLivePreview({
  aggression,
  baseMonths,
  useBestBase,
  closureDuration,
  customContinuations,
  reportData
}: LivePreviewProps) {
  const [estimate, setEstimate] = useState<number | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Calculate a rough estimate based on parameters
  const calculateEstimate = useCallback(() => {
    if (!reportData?.financial_data) {
      setEstimate(null);
      return;
    }

    setIsCalculating(true);

    // Get financial data
    const financialData = reportData.financial_data;
    let totalRevenue = 0;
    let monthCount = 0;

    // Sum up revenue from all years
    Object.keys(financialData).forEach(key => {
      if (key.startsWith('data_')) {
        const yearData = financialData[key];
        if (Array.isArray(yearData)) {
          yearData.forEach((month: any) => {
            const revenue = parseFloat(month.revenue) || parseFloat(month.Revenue) || 0;
            if (revenue > 0) {
              totalRevenue += revenue;
              monthCount++;
            }
          });
        }
      }
    });

    if (monthCount === 0) {
      setEstimate(null);
      setIsCalculating(false);
      return;
    }

    const avgMonthlyRevenue = totalRevenue / monthCount;

    // Growth rate interpolation based on aggression
    const growthRate = (aggression / 100) * 0.15; // 0-15% based on aggression

    // Calculate continuation percentages
    const continuations = customContinuations || {
      officer_salary: 100,
      employee_wages: 100,
      rent: 100,
      utilities: 10 + (aggression / 100) * 30, // 10-40%
      insurance: 80 + (aggression / 100) * 20, // 80-100%
      other_expenses: 10 + (aggression / 100) * 40 // 10-50%
    };

    // Estimate expenses (rough ~60% of revenue typical)
    const estimatedExpenses = avgMonthlyRevenue * 0.6;
    const avgContinuation = (
      (continuations.officer_salary + continuations.employee_wages + 
       continuations.rent + continuations.utilities + 
       continuations.insurance + continuations.other_expenses) / 6
    ) / 100;

    // Calculate lost profits per month
    const continuedExpenses = estimatedExpenses * avgContinuation;
    const projectedRevenue = avgMonthlyRevenue * (1 + growthRate);
    const lostProfitPerMonth = Math.max(projectedRevenue - continuedExpenses, 0);

    // Total lost profits for closure duration
    const totalLostProfits = lostProfitPerMonth * closureDuration;

    // One-time costs estimate
    const annualDiscretionary = avgMonthlyRevenue * 0.2 * 12; // ~20% of revenue
    const shutdownCost = annualDiscretionary * 1.5;
    const restartCost = shutdownCost * 3;
    const oneTimeCosts = shutdownCost + restartCost;

    const totalEstimate = totalLostProfits + oneTimeCosts;

    // Add a small delay for visual feedback
    setTimeout(() => {
      setEstimate(totalEstimate);
      setIsCalculating(false);
    }, 300);
  }, [aggression, baseMonths, useBestBase, closureDuration, customContinuations, reportData]);

  // Debounce the calculation
  useEffect(() => {
    const timer = setTimeout(calculateEstimate, 500);
    return () => clearTimeout(timer);
  }, [calculateEstimate]);

  if (!reportData?.financial_data) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <span className="text-xl">🔮</span>
          <div>
            <p className="text-sm font-medium text-blue-900">Live Estimate</p>
            <p className="text-xs text-blue-600">Updates as you adjust parameters</p>
          </div>
        </div>
        
        <div className="text-right">
          {isCalculating ? (
            <div className="flex items-center space-x-2">
              <svg className="animate-spin h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm text-blue-600">Calculating...</span>
            </div>
          ) : estimate !== null ? (
            <>
              <p className="text-2xl font-bold text-blue-700">
                ${Math.round(estimate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-blue-500">Estimated Total Demand</p>
            </>
          ) : (
            <p className="text-sm text-blue-500">Insufficient data</p>
          )}
        </div>
      </div>

      {estimate !== null && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <div className="flex justify-between text-xs text-blue-600">
            <span>Aggression: {aggression}/100</span>
            <span>Duration: {closureDuration} mo</span>
            <span>Base: {useBestBase ? 'Best' : 'Last'} {baseMonths} mo</span>
          </div>
        </div>
      )}

      <p className="mt-2 text-xs text-blue-500">
        * This is a rough estimate. Click "Run Calculation" for accurate results.
      </p>
    </div>
  );
}
